/**
 * App variant detection — scio3 serves two products from the same bundle:
 * - `optisigns` (default) — full signage CMS on `app.optisigns.com`
 * - `optiedge` — MDM-only spinoff on `app.optiedge.com`
 *
 * Detection priority: VITE_APP_VARIANT env → explicit hostname override →
 * substring match on hostname → default fallback to `'optisigns'`.
 *
 * Default to `'optisigns'` on unknown hostnames to fail safe — a misconfigured
 * staging domain should show the familiar signage nav, not hide it.
 *
 * Spec: docs/specs/19-optiedge-mdm/01-optiedge-mdm-phase-1.md §5.4
 */
export type AppVariant = 'optisigns' | 'optiedge';

const HOSTNAME_OVERRIDES: Record<string, AppVariant> = {
  'app.optiedge.localhost': 'optiedge',
  'optiedge.localhost': 'optiedge',
};

export function detectAppVariant(): AppVariant {
  const fromEnv = import.meta.env.VITE_APP_VARIANT as AppVariant | undefined;
  if (fromEnv === 'optisigns' || fromEnv === 'optiedge') return fromEnv;

  // Test-only override via sessionStorage — Playwright can't redefine
  // `window.location.hostname` on Chromium, so this gives E2E a clean hook.
  // sessionStorage is scoped per tab, so it never leaks across real users.
  if (typeof window !== 'undefined') {
    try {
      const fromSession = window.sessionStorage?.getItem('__app_variant');
      if (fromSession === 'optisigns' || fromSession === 'optiedge') return fromSession;
    } catch {
      // sessionStorage access can throw in sandboxed iframes; ignore.
    }
  }

  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  if (HOSTNAME_OVERRIDES[host]) return HOSTNAME_OVERRIDES[host];
  if (host.includes('optiedge')) return 'optiedge';
  return 'optisigns';
}

/**
 * Hook wrapper for `detectAppVariant()`. The variant is stable for the
 * lifetime of the page (hostname doesn't change), but the hook shape keeps
 * consumer code uniform and leaves room for future reactivity (e.g. env
 * swap in tests).
 */
export function useAppVariant(): AppVariant {
  return detectAppVariant();
}

/**
 * True when this is the white-label / custom-domain build (`scio3-*-branding`, `VITE_IS_WHITE_LABEL=true`).
 * Gate for the host-based branding bootstrap: only this build fetches `getDomainAuth(host)` and themes
 * by host. The normal `app.optisigns.com` build (`false`) keeps the standard OptiSigns look. This is the
 * scio2 `OPS_IS_WHITE_LABEL` analog; per-account behavior stays runtime (`brandingData.isWhiteLabel`).
 */
export function isWhiteLabelBuild(): boolean {
  return String(import.meta.env.VITE_IS_WHITE_LABEL) === 'true';
}
